import { useApi } from '@/api';
import { TableSkeleton } from '@/components';
import {
  ArrowRightIcon,
  DataTable,
  EtherScanPictogram,
  Flex,
  FormattedNumber,
  PercentageValue,
  RelativeDateTime,
  formatNumber,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useMemo } from 'react';

function formatParameter(parameter, value, percents, seconds) {
  if (percents.includes(parameter)) {
    return (
      <PercentageValue
        value={value}
        options={{ formatPercentageOptions: { maximumFractionDigits: 4 } }}
      />
    );
  } else if (seconds.includes(parameter)) {
    return formatNumber(value, { notation: 'standard' }) + ' sec';
  } else {
    return <FormattedNumber value={value} />;
  }
}

export default function ParametersChangesTable({ parameter }) {
  const { pagination, sorting } = useTableProps({
    pageSize: 5,
    initialSorting: '-dai',
    filtersDebounceTime: 50,
    initialFilters: { active: '0' }
  });

  const percents: string[] = useMemo(() => {
    return ['LR', 'Stability fee', 'Chop', 'Buf', 'Cusp', 'Chip', 'Cut'];
  }, []);
  const seconds: string[] = useMemo(() => {
    return ['DC IAM ttl', 'Tail', 'Step'];
  }, []);

  const [data, error] = useApi({
    url: `/ilk-parameters/changes/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      parameter: parameter
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Name',
        getCell: (row) => row.ilk
      },
      {
        header: 'Parameter',
        getCell: (row) => row.parameter,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Value',
        getCell: (row) => (
          <Flex
            flexDirection="row"
            gap="2xs"
            alignContent="center"
            alignItems="center">
            {formatParameter(
              row.parameter,
              row.previous_value,
              percents,
              seconds
            )}
            <ArrowRightIcon size="1" />
            {formatParameter(
              row.parameter,
              row.current_value,
              percents,
              seconds
            )}
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap={['xs', '3xs']} color="muted">
            <RelativeDateTime value={row.datetime} />
            <a
              href={`https://etherscan.io/tx/${row.tx_hash}`}
              target="_blank"
              rel="noreferrer">
              <EtherScanPictogram size="0" />
            </a>
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.block_number + row.ilk + row.parameter}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton rows={5} />;
}
